import type { NotificationOffer } from '#lib/web-notifications.js';

import { useCallback, useEffect, useRef } from 'react';

import { ROUTES } from '@elara/client-core/constants';
import { trackNotificationPromptAnswered } from '@elara/client-core/lib/analytics-events';
import { useStateStore } from '@elara/client-core/lib/signals-utils';
import { cn } from '@elara/client-core/lib/utils';

import { currentNotificationOffer, recordNotificationAsk, requestNotificationPermission } from '#lib/web-notifications.js';
import { useChatsState, useMessageViewState, useNavigate, useNotificationsState } from '#states/hooks.js';
import { NotificationOfferPrompt } from '#ui/chat/NotificationOfferPrompt.js';

/** How long a reply has to be generating before notifications are offered. */
export const REPLY_WAIT_BEFORE_OFFER_MS = 10_000;
/** Gap between the floating offer and the composer (`mb-2` in `ChatInputView`), counted as covered feed. */
const OFFER_GAP_PX = 8;

/**
 * Offers notifications above the composer once a reply has been generating for a while, so a guest
 * who wanders off is brought back when it lands. Shows at most once per reply, never when
 * notifications are on or unsupported, and at most once a week (see `getNotificationOffer`).
 * `ChatInputView` renders it only when no "remember this?" prompt needs the slot.
 */
export function ChatNotificationOffer({ className }: { className?: string }) {
  const chatsState = useChatsState();
  const messageViewState = useMessageViewState();
  const notificationsState = useNotificationsState();
  const { navigate } = useNavigate();
  const observerRef = useRef<ResizeObserver | undefined>(undefined);
  const state = useStateStore({
    offer: undefined as NotificationOffer | undefined,
    isRequesting: false,
  });

  // Start the clock when a reply starts generating; drop the offer when it finishes.
  const isStreaming = chatsState.isStreaming;
  useEffect(() => {
    if (!isStreaming) {
      if (!state.isRequesting) state.offer = undefined;
      return;
    }
    const timer = setTimeout(() => (state.offer = currentNotificationOffer()), REPLY_WAIT_BEFORE_OFFER_MS);
    return () => clearTimeout(timer);
  }, [isStreaming, state]);

  // Publish how much of the feed the offer covers so the "scroll to latest" button can clear it.
  const measureRef = useCallback(
    (element: HTMLDivElement | null) => {
      observerRef.current?.disconnect();
      observerRef.current = undefined;
      if (!element) {
        messageViewState.composerOverlayHeight = 0;
        return;
      }
      const publish = () => {
        messageViewState.composerOverlayHeight = element.offsetHeight + OFFER_GAP_PX;
      };
      publish();
      observerRef.current = new ResizeObserver(publish);
      observerRef.current.observe(element);
    },
    [messageViewState],
  );

  const offer = state.offer;
  if (!offer) return null;

  const accept = async () => {
    if (offer === 'blocked') {
      recordNotificationAsk('open_settings');
      trackNotificationPromptAnswered({ surface: 'chat_banner', outcome: 'open_settings' });
      state.offer = undefined;
      navigate(ROUTES.NOTIFICATIONS);
      return;
    }
    state.isRequesting = true;
    try {
      const permission = await requestNotificationPermission();
      if (permission) trackNotificationPromptAnswered({ surface: 'chat_banner', outcome: permission });
      if (permission === 'granted') await notificationsState.initFCM();
    } finally {
      state.isRequesting = false;
      state.offer = undefined;
    }
  };

  const dismiss = () => {
    recordNotificationAsk('dismissed');
    trackNotificationPromptAnswered({ surface: 'chat_banner', outcome: 'dismissed' });
    state.offer = undefined;
  };

  return (
    <div
      ref={measureRef}
      className={cn('animate-in fade-in slide-in-from-bottom-2 duration-200 ease-out motion-reduce:animate-none', className)}
    >
      <NotificationOfferPrompt
        offer={offer}
        isBusy={state.isRequesting}
        onAccept={() => void accept()}
        onDismiss={dismiss}
      />
    </div>
  );
}
